import React,
{
  useEffect, useRef, useState
}
  from 'react';

import { isNonEmptyArray } from '@rippling/utils/validationUtils';
import { useRouter } from 'next/router';

import Image from '../../components/basic/Image';
import LinkHelper from '../partials/LinkHelper';
import SectionBuilder from '../SectionBuilder';

const getReadTime = (target) => {
  const getWords = (element) => {
    let results = '';
    element?.childNodes?.length &&
      element.childNodes.forEach((node) => {
        if (node.nodeType != 8 && element.tagName !== 'STYLE') {
          results +=
            node.nodeType == 1 ? getWords(node) : `${node.nodeValue} `;
        }
      });

    return results;
  };

  const words = getWords(target);
  const count = words.split(' ').length;
  const averageReadTime = Math.ceil(count / 250);

  return averageReadTime;
};

const slugToText = (slug) => {
  return slug
    .split('-') // Split the string by dashes
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(' '); // Join the words with spaces
};

const GlossaryTerm = ({
  blocks, data, meta, partials,
}) => {
  const router = useRouter();
  const termTopics = meta.glossaryTerms._topic;
  const termCountries = meta.glossaryTerms._country;
  const termTopic = meta.glossaryTerms._topic[0];
  const termTitle = meta.glossaryTerms.title;
  const relatedTerms = meta.glossaryTerms?.relatedTerms || data.relatedTerms;
  const searchLabel = partials.glossaryTermsData.labelsAndMessages.searchLabel;
  const readTimeLabel =
    partials.glossaryTermsData.labelsAndMessages.readTimeLabel;
  const minutesLabel =
    partials.glossaryTermsData.labelsAndMessages.minutesLabel;
  const glossaryLabel =
    partials.glossaryTermsData.labelsAndMessages.glossaryLabel;
  const jumpLinksLabel =
    partials.glossaryTermsData.labelsAndMessages.jumpLinksLabel;
  const relatedTermssLabel =
    partials.glossaryTermsData.labelsAndMessages.relatedTermsLabel;
  const seeRipplingLabel =
    partials.glossaryTermsData.labelsAndMessages.seeRipplingLabel;

  const [jumpLinks, setJumpLinks] = useState([]);
  const [activeJumpLink, setActiveJumpLink] = useState(0);
  const [isMobile, setIsMobile] = useState(null);
  const [isContentAreaVisible, setIsContentAreaVisible] = useState(null);
  const [isMobileJumpLinksOpen, setIsMobileJumpLinksOpen] = useState(false);

  const sectionBuilderContentRef = useRef(null);
  const searchRef = useRef(null);
  const glossaryTermContentArea = useRef(null);

  const readTime = getReadTime(sectionBuilderContentRef.current);

  const GlossaryTermHero = () => {
    return (
      <div className="GlossaryTermHero bg-plum">
        <div className="grid pt-80">
          <div className="row py-48">
            <div className="column flex flex-col gap-24 w-100%">
              <div className="flex items-center gap-8">
                <LinkHelper href="/glossary">
                  <p className="is-style-overline text-white">
                    {glossaryLabel}
                  </p>
                </LinkHelper>
                <div className="imageX w-20 h-20">
                  <Image
                    attributes={{
                      className: 'w-100%',
                      mediaURL:
                        'https://rippling2.imgix.net/icon-chevron-right-white.svg',
                      nonResponsive: true,
                    }}
                  />
                </div>
                <p className="is-style-overline text-white">{termTopic}</p>
              </div>

              <h1 className="h1-xl text-white font-bold">{termTitle}</h1>
              <div className="flex flex-row gap-8">
                {isNonEmptyArray(termTopics) &&
                  termTopics.map((topic, index) => {
                    // global used to be an option and a lot of posts were tagged with it
                    // rather than manually updating this
                    if (topic === 'global') {
                      return null;
                    } else {
                      return (
                        <a
                          key={`topic-${index}`}
                          className="bg-plum-600 rounded-100 py-10 px-16"
                          href={`/glossary?topic=${topic}`}
                        >
                          <p className="is-style-overline text-white">{topic}</p>
                        </a>
                      );
                    }
                  })}

                {isNonEmptyArray(termCountries) &&

                  termCountries.map((country, index) => {
                    if (country === 'global') {
                      return null;
                    } else {
                      return (
                        <a
                          key={`country-${index}`}
                          className="bg-plum-600  rounded-100 py-10 px-16 flex items-center"
                          href={`/glossary?country=${country}`}
                        >
                          <p className="is-style-overline text-white">{slugToText(country)}</p>

                        </a>
                      );
                    }
                  })}
              </div>
              <div className="flex flex-col gap-6">
                <p className="is-style-overline text-plum-400">
                  {readTimeLabel}
                </p>

                <p className="is-style-p2 text-plum-200 font-bold">
                  {readTime} {minutesLabel}
                </p>
              </div>

              <div className="max-w-625 w-100%">
                <input
                  ref={searchRef}
                  className="flex bg-tertiary-gray1 p-16 h-64 w-100% rounded-4"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchRef?.current?.value) {
                      router.replace(`/glossary?query=${searchRef.current.value}`);
                    }
                  }}
                  placeholder={searchLabel}
                  type="text"
                />

                <div
                  className="flex items-center justify-center w-56 h-56 bg-yellow absolute top-4 right-4 rounded-4 absolute cursor-pointer"
                  onClick={() => {
                    if (searchRef?.current?.value) {
                      router.replace(`/glossary?query=${searchRef.current.value}`);
                    }
                  }}
                >
                  <div className="imageX w-22 h-22">
                    <Image
                      attributes={{
                        className: 'w-100%',
                        mediaURL:
                          'https://rippling2.imgix.net/icon-magnifying-glass-1.svg',
                        nonResponsive: true,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const GlossaryTermJumpLinks = () => {
    return (
      <div className="GlossaryTermJumpLinks md:flex hidden">
        {jumpLinks && (
          <div className="flex flex-col gap-24">
            <p className="is-style-p1 font-bold text-plum-800">
              {jumpLinksLabel}
            </p>

            <div className="border-t-1 border-tertiary-gray2">
              {jumpLinks.map((jumpLink, index) => {
                const clickHandler = () => {
                  if (jumpLink?.getBoundingClientRect().y) {
                    const paddingHeight = 100;

                    const getEleY = () =>
                      window.scrollY -
                      paddingHeight +
                      jumpLink.getBoundingClientRect().y;

                    window.scrollTo({
                      behavior: 'smooth',
                      left: 0,
                      top: getEleY(),
                    });
                  }
                };

                return (
                  <div
                    key={`jumpLink-${index}`}
                    className="py-16 border-b-1 cursor-pointer border-tertiary-gray2"
                    onClick={clickHandler}
                  >
                    <p
                      dangerouslySetInnerHTML={{ __html: jumpLink.innerHTML }}
                      className={`is-style-p2 text-plum-800 ${
                        activeJumpLink === index ? 'font-bold' : ''
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const GlossaryTermMobileJumpLinks = () => {
    return (
      <div
        className={`GlossaryTermMobileJumpLinks md:hidden w-100% bg-white z-1000 ${
          isContentAreaVisible
            ? 'fixed top-70 border-t-1 border-tertiary-gray2'
            : ''
        }`}
      >
        {jumpLinks && (
          <div className="bg-white border-b-1 border-tertiary-gray2">
            <div className="grid flex flex-col overflow-hidden">
              <div
                className="flex flex-row items-center w-100% justify-between h-56"
                onClick={() => {
                  setIsMobileJumpLinksOpen(!isMobileJumpLinksOpen);
                }}
              >
                <div className="flex items-center">
                  <p className="is-style-p2 font-bold text-plum-800 opacity-70">
                    {jumpLinksLabel}
                  </p>
                </div>

                <div className="h-32 w-32 rounded-2 bg-tertiary-gray1 flex items-center justify-center">
                  <div
                    className={`imagex w-10 h-10 transition duration-250 ${
                      isMobileJumpLinksOpen ? 'rotate-180' : ''
                    }`}
                  >
                    <Image
                      attributes={{
                        className: 'w-100%',
                        mediaURL:
                          'https://rippling.imgix.net/images/chevron-down.svg',
                        nonResponsive: true,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`${
                  isMobileJumpLinksOpen
                    ? 'h-fit overflow-y-scroll'
                    : 'h-0 overflow-hidden '
                }`}
              >
                <div className="border-t-1 border-tertiary-gray2">
                  {jumpLinks.map((jumpLink, index) => {
                    const clickHandler = () => {
                      if (jumpLink?.getBoundingClientRect().y) {
                        const paddingHeight = 100;

                        const getEleY = () =>
                          window.scrollY -
                          paddingHeight +
                          jumpLink.getBoundingClientRect().y;

                        window.scrollTo({
                          behavior: 'smooth',
                          left: 0,
                          top: getEleY(),
                        });

                        setIsMobileJumpLinksOpen(false);
                      }
                    };

                    return (
                      <div
                        key={`mobileJumpLink-${index}`}
                        className="py-16 border-b-1 border-tertiary-gray2"
                        onClick={clickHandler}
                      >
                        <p
                          dangerouslySetInnerHTML={{ __html: jumpLink.innerHTML }}

                          className={`is-style-p2 text-plum-800 ${
                            activeJumpLink === index ? 'font-bold' : ''
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const GlossaryTermRelatedTerms = () => {
    return (
      <div className="GlossaryTermRelatedTerms w-100%">
        {relatedTerms && (
          <div className="flex flex-col gap-24 w-100%">
            <p className="is-style-p1 font-bold text-plum-800">
              {relatedTermssLabel}
            </p>
            <div className="flex flex-col gap-8">
              {relatedTerms.map((term, index) => {
                const slug = term?._slug || term.slug;
                const name = term?.name || term.wp.title;

                return (
                  <LinkHelper
                    key={`relatedTerm-${index}`}
                    className="py-8 px-16 border-1 border-tertiary-gray2"
                    href={`/glossary/${slug}`}
                  >
                    <p className="is-style-p2 text-plum-800">{name}</p>
                  </LinkHelper>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const contentSection = sectionBuilderContentRef.current;
    const jumpLinksCollection = contentSection?.querySelectorAll('h2');
    const tempJumpLinks = jumpLinksCollection ? [...jumpLinksCollection] : [];
    setIsMobile(window.innerWidth < 1024);

    const jumpLinkObserverOptions = { rootMargin: `0px 0px -${window.innerHeight * 0.8}px 0px` };

    for (const [index, jumpLink] of tempJumpLinks.entries()) {
      jumpLink.dataset.jumpLink = index;
    }

    const jumpLinkObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveJumpLink(Number(entry.target.dataset.jumpLink));
        }
      }
    }, jumpLinkObserverOptions);

    tempJumpLinks.map((jumpLink) => {
      jumpLinkObserver.observe(jumpLink);
    });

    setJumpLinks(tempJumpLinks);

    if (isMobile) {
      const contentAreaObserverOptions = { rootMargin: `0px 0px -${window.innerHeight - 70}px 0px` };

      const contentAreaObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setIsContentAreaVisible(true);
          glossaryTermContentArea.current.style.paddingTop = '56px';
        } else {
          setIsContentAreaVisible(false);
          glossaryTermContentArea.current.style.paddingTop = null;
        }
      }, contentAreaObserverOptions);
      contentAreaObserver.observe(glossaryTermContentArea.current);
    }

    const resizeHandler = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', resizeHandler);

    return () => {
      tempJumpLinks.map((jumpLink) => {
        jumpLinkObserver.unobserve(jumpLink);
      });

      window.removeEventListener('resize', resizeHandler);
    };
  }, [
    setJumpLinks,
    activeJumpLink,
    setActiveJumpLink,
    isMobile,
  ]);

  return (
    <div className="GlossaryTerm">
      <GlossaryTermHero />

      <div ref={glossaryTermContentArea} className="flex flex-col">
        <GlossaryTermMobileJumpLinks />

        <div className="grid">
          <div className="row display-grid grid-cols-1 md:grid-cols-12 md:pt-80 pb-40 sm:pb-80 md:pb-160">
            <div className="column md:col-span-4 ">
              <div className="w-100% md:max-w-325 h-100%">
                <div className="flex flex-col gap-40 sticky top-104">
                  <GlossaryTermJumpLinks />

                  <div className="md:flex hidden w-100%">
                    <GlossaryTermRelatedTerms />
                  </div>

                  <LinkHelper
                    className="md:flex justify-center bg-yellow py-16 w-100% rounded-4 hidden"
                    href="/request-demo"
                  >
                    <p className="is-style-p2 text-plum-800 font-medium">
                      {seeRipplingLabel}
                    </p>
                  </LinkHelper>
                </div>
              </div>
            </div>

            <div
              ref={sectionBuilderContentRef}
              className="glossary-blog-content-wrapper column md:col-span-8 pt-40 sm:pt-80 md:pt-0"
            >
              <SectionBuilder blocks={blocks} />
            </div>

            <div className="md:hidden flex w-100% pt-32">
              <GlossaryTermRelatedTerms />
            </div>
          </div>
        </div>
      </div>

      <SectionBuilder blocks={partials.prefooter.blocks} />
    </div>
  );
};

export default GlossaryTerm;
